import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { useQuery } from "react-query";
import { useBundledState } from "../../../hooks/hooks";
import __ from "../../../utils/utils";
import { AppLoader } from "../loaders/AppLoader";
import ContextLikeMenu from "../menu/ContextLikeMenu";

interface Props<T> {
  className?: string;
  label?: string;
  placeholder?: string;
  id: string;
  fetch(search: string): Promise<T>;
  Render(props: { data: T; close(): void }): JSX.Element;
  disabled?: boolean;
}

/*
  ToDo:
  - fix a bug where this stops searching after the first error:ed query
*/
const SearchFieldSlideDown = <T,>(props: Props<T>) => {
  const search = useBundledState("");
  const showResults = useBundledState(false);
  const [customLoadingState, setCustomLoadingState] = useState(false);
  const searchRef = useRef(search.value);

  const res = useQuery(
    ["searchResults", props.id],
    () => {
      return props.fetch(searchRef.current);
    },
    {
      enabled: false,
    }
  );

  const timeoutDelay = 400;

  const { Render } = props;
  useEffect(() => {
    searchRef.current = search.value;
  }, [search.value]);
  useEffect(() => {
    const searchQueryEntered = search.value.length > 0;
    setCustomLoadingState(searchQueryEntered);
    showResults.set(searchQueryEntered);

    const timeout = setTimeout(() => {
      if (searchQueryEntered) {
        res.refetch();
      }
      setCustomLoadingState(false);
    }, timeoutDelay);

    return () => {
      clearTimeout(timeout);
    };
  }, [search.value, res.refetch]);

  const isLoading = res.isLoading || customLoadingState;
  const showLoadingIndicator =
    res.isLoading || (res.data === undefined && customLoadingState);

  return (
    <div
      className={__.classNames(
        "relative max-w-full",
        props.disabled && "pointer-events-none opacity-80",
        props.className
      )}
    >
      <p className={"flex w-full flex-col gap-2"}>
        {props.label && <label htmlFor={props.id}>{props.label}</label>}

        <span className="grid w-full grid-cols-[auto,minmax(0,1fr)] items-center justify-between gap-2 rounded border border-main-bg-light border-transparent bg-bg-base-layer px-4 pr-2 focus-within:border-black lg:border-bg-base-layer">
          <BiSearch size={25} />
          <input
            className="max-w-full bg-bg-base-layer p-2 focus:outline-none"
            id={props.id}
            onFocus={() => showResults.set(search.value.length > 0)}
            type="text"
            value={search.value}
            onChange={(e) => search.set(e.target.value)}
            placeholder={props.placeholder}
            disabled={props.disabled}
            aria-label={props.label ? undefined : props.placeholder}
          />
        </span>
      </p>
      <div aria-live="polite">
        <AnimatePresence>
          {showResults.value && (
            <ContextLikeMenu
              onOutsideClick={() => showResults.set(false)}
              className="absolute flex min-h-[100px] w-full justify-center border-2"
            >
              {showLoadingIndicator && (
                <AppLoader className="absolute h-10 w-10 border" />
              )}
              <motion.span
                data-test-id="search-results"
                data-search-results-for={props.id}
                aria-label="Sökresultat"
                className="h-full w-full"
                animate={{
                  opacity: isLoading ? 0.4 : 1,
                }}
              >
                {!res.isError && res.data && (
                  <Render
                    data={res.data}
                    close={() => showResults.set(false)}
                  />
                )}
                {res.isError && <p>Något gick fel vid sökningen</p>}
              </motion.span>
            </ContextLikeMenu>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SearchFieldSlideDown;
